/**
 * REALTIME AUF `projects` — was ein Ereignis an der Projektliste aendert.
 *
 * ─── Der Anlass ─────────────────────────────────────────────────────────────
 * `projects` steht in der Publikation `supabase_realtime`. Flottenweit kommen
 * rund 866 UPDATEs am Tag an (jeder Autosave jedes Mitarbeiters, etwa zwei
 * Sekunden nach dem letzten Tastendruck) — die EIGENEN Speichervorgaenge
 * eingeschlossen, die als Echo zurueckkommen. Bis 10/2026 lud App.tsx fuer
 * jedes davon `loadSingleProjectOverview`: den ganzen Blob, alle Maengel mit
 * Bild-URLs und die Positionen. Der Riegel `saveStatus === 'saving'` las
 * einen eingefrorenen Wert aus der Closure und griff praktisch nie.
 *
 * ─── Die Regeln ─────────────────────────────────────────────────────────────
 *  1. BEKANNTER STAND wird uebersprungen (`istBekannterStand`): der
 *     LISTENEINTRAG selbst traegt schon diesen Stand (sein `updated_at` ist
 *     nicht aelter als das Ereignis) und keine Listenspalte weicht ab. Das
 *     eigene Echo faellt genau hierunter — nach dem Speichern spreizt
 *     App.handleQueueFlushed die Serverzeile in den Eintrag. Der Anker aus
 *     projectVersionAnchor reicht NICHT: ihn setzt auch das Oeffnen eines
 *     Projekts (loadProjectById), ohne dass der Listeneintrag davon etwas
 *     haette (Codex-Review 21.10.2026). Die Spalten werden mitverglichen,
 *     weil manche Schreibwege `updated_at` NICHT anfassen (Uebergabe,
 *     Exportstand, Termine, CRM-Kunde) — dort bliebe der Zeitstempel gleich,
 *     die Zeile aber nicht.
 *  2. SAMMELN statt sofort laden (`erzeugeProjektSammler`): drei Sekunden
 *     Ruhe je Schub (hoechstens 15 s Aufschub), dann EIN Abruf fuer alle
 *     vorgemerkten Projekte. Verborgen wird nur vorgemerkt; abgearbeitet wird
 *     beim Sichtbarwerden. Mehr als 20 Projekte: ein Voll-Load (gedrosselt wie
 *     das Nachholen, authRefreshPolicy), sonst doch einzeln. Eine Antwort, die
 *     erst nach dem Abbau (Abmelden, Nutzerwechsel) ankommt, wird verworfen.
 *  4. NIE ZURUECK (`istVeralteteAntwort`): eine spaete Einzelantwort ersetzt
 *     keinen Eintrag, der schon einen neueren Stand traegt — etwa aus einem
 *     Voll-Load, der waehrend des Abrufs lief.
 *  3. WENIGER LADEN: der Eintrag bekommt die Angleichungen der Projektliste
 *     (`bildeListenEintrag`), nicht die Form der Einzeluebersicht. Kein
 *     Verbraucher der Liste liest Maengel oder Blob-Zweige ausser `metadata`
 *     (Pruefung vom 21.10.2026, siehe `realtimeProjektLader.ts`). Bringt das
 *     Ereignis den Blob selbst mit, kommt nur `project_services` aus der
 *     Datenbank, und der Blob bleibt ganz (`zeileAusPayload`); fehlt er
 *     (Payload ueber 1 MB, unveraenderter TOAST-Wert bei einem reinen
 *     Spalten-Update), wird die Listenzeile geladen.
 *
 * ─── Seit Nr. 175: Signale statt Zeilen ────────────────────────────────────
 * Mit `projekt_signale` (supabase/projekt_signale.sql) hoert App.tsx nicht
 * mehr auf `projects`, sondern auf die Signale: Kennung, Zeitpunkt, Grabstein
 * — kein Blob ueber den Websocket. Der Sammler bleibt derselbe; vorgemerkt
 * wird `{ id, signal_zeile, geaendert_am }` (projektListeDelta.signalVormerkung),
 * bekannt ist eine Signalzeile, die vor dem Abruf des Eintrags gesehen wurde
 * (logische Uhr, projektListeDelta), geladen wird immer die Listenzeile (kein
 * Blob im Payload). Regel 1 (`istBekannterStand`) und Regel 4
 * (`istVeralteteAntwort`) gelten nur noch ohne Nr. 175 — mit Signalen ordnet
 * die Abruf-Marke die Antworten.
 *
 * Rein und ohne DOM, Zeitgeber werden injiziert.
 */

import { ENTBEHRLICHE_SPALTEN, PROJECT_COLUMNS_LIST, baseColumn } from './queryColumns';
import { bestandteilFelderAusZeile } from './projectLv';
import { applyLinkColumns } from './projectLink';
import { markiereListenAuszug } from './listenAuszug';

/** Eine Zeile, wie PostgREST oder Realtime sie liefern. */
export type Zeile = Record<string, unknown>;

const istObjekt = (x: unknown): x is Record<string, unknown> =>
    typeof x === 'object' && x !== null && !Array.isArray(x);

// ═══════════════════════════════════════════════════════════════════════════
// ZEITSTEMPEL
// ═══════════════════════════════════════════════════════════════════════════

/**
 * `timestamptz` als Mikrosekunden seit 1970, oder `null`.
 *
 * Mikrosekunden statt `Date.parse`: Postgres speichert sechs Nachkommastellen,
 * `Date` kennt drei. Zwei Speichervorgaenge in derselben Millisekunde waeren
 * sonst gleich alt. Verstanden werden die JSON-Form von PostgREST und Realtime
 * (`2026-10-21T08:15:30.123456+00:00`) und die Textform von Postgres
 * (`2026-10-21 08:15:30.123456+00`).
 */
export const zeitstempelMikros = (wert: unknown): number | null => {
    if (typeof wert !== 'string') return null;
    const m = /^(\d{4}-\d{2}-\d{2})[T ](\d{2}:\d{2}:\d{2})(?:\.(\d+))?\s*(Z|[+-]\d{2}(?::?\d{2})?)?$/i.exec(wert.trim());
    if (!m) return null;
    const [, tag, zeit, bruch = '', zone] = m;
    let versatz = 'Z';
    if (zone && zone.toUpperCase() !== 'Z') {
        const ziffern = zone.slice(1).replace(':', '');
        versatz = `${zone[0]}${ziffern.slice(0, 2)}:${ziffern.slice(2, 4) || '00'}`;
    }
    const ms = Date.parse(`${tag}T${zeit}${versatz}`);
    if (Number.isNaN(ms)) return null;
    return ms * 1000 + Number(`${bruch}000000`.slice(0, 6));
};

/** Ist `neu` hoechstens so neu wie `bekannt`? Unlesbares gilt nie als bekannt. */
export const istNichtNeuer = (neu: unknown, bekannt: unknown): boolean => {
    if (typeof neu === 'string' && neu !== '' && neu === bekannt) return true;
    const a = zeitstempelMikros(neu);
    const b = zeitstempelMikros(bekannt);
    return a !== null && b !== null && a <= b;
};

// ═══════════════════════════════════════════════════════════════════════════
// DAS EREIGNIS
// ═══════════════════════════════════════════════════════════════════════════

export type ProjektAenderung =
    | { art: 'loeschen'; id: string }
    | { art: 'merken'; id: string; neu: Zeile; fehler?: unknown }
    | { art: 'unbekannt' };

/** Liest ein `postgres_changes`-Ereignis auf `projects`. */
export const leseProjektAenderung = (payload: unknown): ProjektAenderung => {
    if (!istObjekt(payload)) return { art: 'unbekannt' };
    if (payload.eventType === 'DELETE') {
        const alt = payload.old;
        return istObjekt(alt) && typeof alt.id === 'string' && alt.id ? { art: 'loeschen', id: alt.id } : { art: 'unbekannt' };
    }
    const neu = payload.new;
    if (istObjekt(neu) && typeof neu.id === 'string' && neu.id) return { art: 'merken', id: neu.id, neu, fehler: payload.errors };
    return { art: 'unbekannt' };
};

// ═══════════════════════════════════════════════════════════════════════════
// REGEL 1 — BEKANNTER STAND
// ═══════════════════════════════════════════════════════════════════════════

/** Die Spalten der Projektliste ohne den Blob — dieselbe Quelle wie der Listen-Load. */
export const LISTEN_SPALTEN: readonly string[] = PROJECT_COLUMNS_LIST
    .map((eintrag) => baseColumn(eintrag))
    .filter((spalte) => spalte !== 'data');

/** Was die Datenbank nicht haben MUSS (Spalten-Rueckfall) — fehlt es im Payload, ist das kein Mangel. */
const ENTBEHRLICH_PROJEKTE = new Set(
    ENTBEHRLICHE_SPALTEN.filter((s) => s.tabelle === 'public.projects').map((s) => s.spalte),
);

/** Gleich im Sinn der Liste: `null` und `undefined` sind dasselbe, JSON wird als Text verglichen. */
const gleicherWert = (a: unknown, b: unknown): boolean => {
    if ((a === null || a === undefined) && (b === null || b === undefined)) return true;
    if (a === b) return true;
    if (typeof a === 'object' || typeof b === 'object') {
        try { return JSON.stringify(a) === JSON.stringify(b); } catch { return false; }
    }
    return false;
};

/**
 * Kennt die LISTE diesen Stand schon? Dann wird das Ereignis uebersprungen.
 *
 * Gefragt wird der Listeneintrag selbst, nicht der Anker: der Anker sagt nur,
 * dass DIESER TAB den Stand irgendwo gesehen hat — `loadProjectById` setzt ihn
 * auch beim blossen Oeffnen, und der Listeneintrag behielte dann den alten
 * Blob und die alten Zaehler. Ohne Listeneintrag wird geladen; das ist auch
 * das Echo der eigenen Neuanlage: die steht noch nicht in der Liste und kommt
 * genau ueber dieses Ereignis hinein.
 */
export const istBekannterStand = (neu: Zeile, eintrag: Zeile | undefined): boolean => {
    if (!eintrag) return false;
    if (!istNichtNeuer(neu.updated_at, eintrag.updated_at)) return false;
    for (const spalte of LISTEN_SPALTEN) {
        if (spalte === 'updated_at' || spalte === 'created_at') continue;
        // Fehlt die Spalte im Payload, gibt es nichts zu vergleichen.
        if (!(spalte in neu)) continue;
        if (!gleicherWert(neu[spalte], eintrag[spalte])) return false;
    }
    return true;
};

// ═══════════════════════════════════════════════════════════════════════════
// REGEL 3 — DER LISTENEINTRAG
// ═══════════════════════════════════════════════════════════════════════════

/**
 * Die Listenzeile aus dem Payload — oder `null`, wenn er dafuer nicht taugt
 * (dann wird geladen).
 *
 * Uebernommen werden genau die Spalten der Projektliste und der Blob (er
 * kommt ohnehin mit). Auch mit ganzem Blob bleibt der Eintrag ein AUSZUG —
 * ohne die Kindtabellen (Positionen, Notizen, Plaene, Maengel …);
 * `bildeListenEintrag` kennzeichnet ihn, und `saveProjectToCloud` weist ihn ab
 * (listenAuszug.ts, Vorfall 21.10.2026). Wer aus der Liste aendert, laedt
 * voll (folgeterminService.ts).
 */
export const zeileAusPayload = (neu: unknown, fehler?: unknown): Zeile | null => {
    if (!istObjekt(neu) || typeof neu.id !== 'string' || !neu.id) return null;
    // Realtime meldet hier u. a. „Payload Too Large" — dann fehlen Felder.
    if (Array.isArray(fehler) && fehler.length > 0) return null;
    const data = neu.data;
    if (!istObjekt(data) || !istObjekt(data.metadata)) return null;
    const zeile: Zeile = {};
    for (const spalte of LISTEN_SPALTEN) {
        if (spalte in neu) zeile[spalte] = neu[spalte];
        else if (!ENTBEHRLICH_PROJEKTE.has(spalte)) return null;
    }
    zeile.data = data;
    return zeile;
};

/**
 * Eine Zeile aus `project_services` in die Form von `globalData.services`.
 *
 * DIESELBE Abbildung wie im Listen-Load (cloudService.doLoadProjectsFromCloud)
 * — sie steht dort nicht exportiert, und cloudService.ts war bei diesem Umbau
 * gesperrt. Die Bedingung ist dieselbe wie dort: JEDES Feld, das
 * `saveProjectToCloud` schreibt, muss hier gelesen werden (min_hours,
 * Verzicht, Monatspauschale, Herkunft, Paket, Stunden), sonst leert ein
 * Speichern aus dem Listeneintrag es. `realtimeProjectSync.test.ts` haelt jede
 * Spalte aus `PROJECT_SERVICES_COLUMNS` gegen diese Abbildung.
 */
export const mappeLeistung = (dbService: Zeile): Zeile => ({
    id: dbService.id,
    lvPosition: dbService.lv_position,
    title: dbService.title,
    unitPrice: dbService.unit_price,
    quantityContracted: dbService.quantity_contracted,
    quantityUsed: dbService.quantity_used,
    quantityBilled: dbService.quantity_billed,
    assignment: dbService.assignment || 'none',
    unit: dbService.unit || 'Stk.',
    is_optional: dbService.is_optional || false,
    status: dbService.status,
    category: dbService.category,
    clientLvPosition: dbService.client_lv_position,
    minHours: dbService.min_hours ?? undefined,
    quantityWaived: dbService.quantity_waived ?? undefined,
    waivedEntryIds: dbService.waived_entry_ids ?? undefined,
    waivedReason: dbService.waived_reason ?? undefined,
    waivedAt: dbService.waived_at ?? undefined,
    waivedBy: dbService.waived_by ?? undefined,
    flatRate: dbService.flat_rate ?? undefined,
    transferredFrom: dbService.transferred_from ?? undefined,
    ...bestandteilFelderAusZeile(dbService),
});

/**
 * Baut aus einer Zeile den Eintrag der Projektliste — dieselben Angleichungen
 * wie der Listen-Load: Folgetermin aus der Spalte, Kategorie aus den Spalten,
 * `globalData.services` aus `project_services`, Verknuepfung aus den Spalten
 * (Nr. 71).
 *
 * Die Zeile darf entweder `metadata` (Alias aus `data->metadata`, so liefert
 * sie die Listenabfrage) oder `data` (der Blob aus dem Payload) tragen. Die
 * Eingabe wird NICHT veraendert: Blob, Metadaten und die angepassten Zweige
 * werden flach kopiert.
 *
 * `data` traegt die Kennung des Listenauszugs (listenAuszug.ts) — wie jeder
 * Eintrag der Projektliste, auch mit ganzem Blob aus dem Payload: es fehlen
 * die Kindtabellen. `saveProjectToCloud` weist ihn ab.
 *
 * Liefert den Eintrag und die Kennung der Mutter, falls es eine Tochter ist —
 * den Namen traegt der Aufrufer nach (`setLinkedParentName`).
 */
export const bildeListenEintrag = (zeile: Zeile): { eintrag: Zeile; mutterId: string | null } => {
    const { metadata: aliasMetadata, ...rest } = zeile;
    const p: Zeile = { ...rest };
    const blob = istObjekt(zeile.data) ? zeile.data : null;
    const quelle = istObjekt(aliasMetadata) ? aliasMetadata : (blob && istObjekt(blob.metadata) ? blob.metadata : null);

    const metadata: Zeile = quelle ? { ...quelle } : { globalData: {} };
    p.data = markiereListenAuszug(blob && !istObjekt(aliasMetadata) ? { ...blob, metadata } : { metadata });

    if (istObjekt(metadata.intervalConfig)) {
        metadata.intervalConfig = {
            ...metadata.intervalConfig,
            nextScheduledDate: p.next_scheduled_date || metadata.intervalConfig.nextScheduledDate,
        };
    }
    metadata.projectCategory = p.category || metadata.projectCategory || '';
    metadata.projectSubcategory = p.subcategory || metadata.projectSubcategory || '';
    metadata.category = p.category || metadata.category || '';
    metadata.subcategory = p.subcategory || metadata.subcategory || '';

    const leistungen = Array.isArray(p.project_services) ? p.project_services.filter(istObjekt) : [];
    if (leistungen.length > 0) {
        metadata.globalData = {
            ...(istObjekt(metadata.globalData) ? metadata.globalData : {}),
            services: leistungen.map(mappeLeistung),
        };
    } else if (istObjekt(metadata.globalData)) {
        // `applyLinkColumns` schreibt in `globalData` — nicht in den Zweig des
        // Payloads, den vielleicht noch jemand haelt.
        metadata.globalData = { ...metadata.globalData };
    }

    const mutterId = applyLinkColumns(p);
    return { eintrag: p, mutterId };
};

/** Wann die Einzelantwort angefragt wurde und wann der letzte Voll-Load begann. */
export interface AntwortKontext {
    abfrageBeginnMs: number;
    /** Beginn des letzten erfolgreichen Voll-Loads; `null` = keiner. */
    vollLoadMs: number | null;
}

/**
 * Ist die Einzelantwort `neu` aelter als das, was die Liste schon hat?
 *
 *  - aelteres `updated_at` als der Eintrag: ja, immer;
 *  - gleiches `updated_at`, aber ein Voll-Load hat NACH der Anfrage begonnen:
 *    ja — er weiss mindestens dasselbe, und Spalten-Updates ohne neuen
 *    Zeitstempel kennt nur er;
 *  - unlesbare Zeitstempel: nein (wie bisher wird ersetzt).
 *
 * Fuer ein Projekt, das (noch) nicht in der Liste steht: ja, wenn ein
 * Voll-Load nach der Anfrage begann — er kannte das Projekt schon und hat es
 * trotzdem nicht geliefert (geloescht, Rechte entzogen).
 */
export const istVeralteteAntwort = (neu: Zeile, alt: Zeile | undefined, kontext: AntwortKontext): boolean => {
    const vollLoadDanach = kontext.vollLoadMs !== null && kontext.vollLoadMs > kontext.abfrageBeginnMs;
    if (!alt) return vollLoadDanach;
    const a = zeitstempelMikros(neu.updated_at);
    const b = zeitstempelMikros(alt.updated_at);
    if (a === null || b === null) return false;
    if (a < b) return true;
    return a === b && vollLoadDanach;
};

/**
 * Fuehrt frisch geladene Eintraege in die Liste ein — dieselbe Regel wie
 * bisher in App.handleRealtimeProjectUpdate: vorhandene ersetzen, neue vorne
 * einsortieren (juengste Aenderung zuerst). `alsEingefuegt` bekommt jede
 * Kennung, die NEU in die Liste kam (Merker fuer den naechsten Voll-Load).
 *
 * Neu ist nur die Regel 4: eine Antwort, die `istVeralteteAntwort` ist,
 * bleibt draussen (Codex-Review 21.10.2026: ein spaeter V1 ueberschrieb den
 * V2 eines Voll-Loads, und ohne weiteres Ereignis blieb es dabei).
 */
export const fuegeRealtimeEintraegeEin = <T extends { id?: unknown; updated_at?: unknown }>(
    prev: T[],
    eintraege: T[],
    alsEingefuegt: (id: string) => void,
    kontext?: AntwortKontext,
): T[] => {
    if (eintraege.length === 0) return prev;
    const neu = new Map<string, T>();
    for (const e of eintraege) if (typeof e.id === 'string') neu.set(e.id, e);
    let geaendert = false;
    let ergebnis = prev.map((p) => {
        const ersatz = typeof p.id === 'string' ? neu.get(p.id) : undefined;
        if (!ersatz) return p;
        neu.delete(p.id as string);
        if (kontext && istVeralteteAntwort(ersatz, p, kontext)) return p;
        geaendert = true;
        return ersatz;
    });
    if (kontext) {
        for (const [id, e] of neu) if (istVeralteteAntwort(e, undefined, kontext)) neu.delete(id);
    }
    if (neu.size === 0 && !geaendert) return prev;
    if (neu.size > 0) {
        for (const id of neu.keys()) alsEingefuegt(id);
        const zeit = (x: T): number => new Date(String(x.updated_at)).getTime();
        ergebnis = [...neu.values(), ...ergebnis].sort((a, b) => zeit(b) - zeit(a));
    }
    return ergebnis;
};

// ═══════════════════════════════════════════════════════════════════════════
// REGEL 2 — SAMMELN
// ═══════════════════════════════════════════════════════════════════════════

/** Ruhe nach dem letzten Ereignis, bevor geladen wird. */
export const SAMMEL_RUHE_MS = 3000;
/** Laenger wird ein Schub nicht aufgeschoben, auch wenn es weiter tropft. */
export const SAMMEL_HOECHSTENS_MS = 15000;
/** Nach einem gescheiterten Abruf (kein Netz) erst so viel spaeter wieder — oder beim Anstossen. */
export const SAMMEL_WIEDERHOLUNG_MS = 60000;
/** Ab so vielen vorgemerkten Projekten ist ein Voll-Load der kuerzere Weg. */
export const VOLL_LADEN_AB = 20;

/** Ein vorgemerktes Projekt. */
export interface Vormerkung {
    /** `payload.new` des letzten Ereignisses. */
    neu: Zeile;
    /** `payload.errors` des letzten Ereignisses. */
    fehler?: unknown;
    /** Wann zuletzt gemeldet. */
    zuletztMs: number;
}

/**
 * `T`: die Form der geladenen Eintraege. Der Sammler selbst liest sie nicht —
 * er reicht weiter, was `laden` liefert, an `uebernehmen`. Generisch, damit die
 * App ihre Listeneintraege (`ListProject`) ohne Umweg ueber `Zeile` bekommt.
 */
export interface SammlerOptionen<T extends Zeile = Zeile> {
    istVersteckt: () => boolean;
    /** Kennt die Liste den Stand schon (`istBekannterStand` gegen den Listeneintrag)? */
    istBekannt: (id: string, neu: Zeile) => boolean;
    /** Darf das Projekt gerade nicht angefasst werden (eigenes Speichern laeuft)? Dann spaeter. */
    istBeschaeftigt?: (id: string) => boolean;
    /** Laedt die Vormerkungen; wirft, wenn der Abruf scheitert. Schreibt NICHTS in den Zustand. */
    laden: (vormerkungen: Map<string, Vormerkung>) => Promise<T[]>;
    /**
     * Uebernimmt das Geladene in die Liste. Wird nur gerufen, solange der
     * Sammler lebt — eine Antwort, die nach `abbauen` (Abmelden,
     * Nutzerwechsel) ankommt, geht nirgendwohin. `vormerkungen` sind die
     * abgefragten — im Signalmodus traegt jede den Stand ihres Signals, und
     * was angefragt, aber nicht geliefert wurde, ist geloescht oder entzogen
     * (projektListeDelta.ordneSignalAntwort).
     */
    uebernehmen: (eintraege: T[], abfrageBeginnMs: number, vormerkungen: ReadonlyMap<string, Vormerkung>) => void;
    /**
     * Voll-Load statt Einzelaktualisierung. `null`, wenn er gerade nicht
     * erlaubt ist (Takt); sonst ein Promise mit dem Beginn des Loads, `null`
     * bei einem Fehlschlag.
     */
    vollLaden: () => Promise<number | null> | null;
    jetzt?: () => number;
    schedule?: (fn: () => void, ms: number) => unknown;
    cancel?: (handle: unknown) => void;
}

export interface ProjektSammler {
    /** Ein INSERT/UPDATE vormerken. */
    melde: (id: string, neu: Zeile, fehler?: unknown) => void;
    /** Ein DELETE: nichts mehr laden. */
    vergiss: (id: string) => void;
    /** Tab wieder sichtbar oder wieder online: jetzt abarbeiten, ohne Ruhezeit. */
    anstossen: () => void;
    /** Ein Voll-Load ab `beginnMs` ist durch — was vorher gemeldet wurde, steht drin. */
    nachVollLoad: (beginnMs: number) => void;
    abbauen: () => void;
    /** Kennungen, die gerade vorgemerkt sind. */
    offen: () => string[];
}

export const erzeugeProjektSammler = <T extends Zeile = Zeile>(opts: SammlerOptionen<T>): ProjektSammler => {
    const jetzt = opts.jetzt ?? (() => Date.now());
    const schedule = opts.schedule ?? ((fn: () => void, ms: number) => setTimeout(fn, ms));
    const cancel = opts.cancel ?? ((h: unknown) => clearTimeout(h as ReturnType<typeof setTimeout>));

    const vorgemerkt = new Map<string, Vormerkung>();
    let timer: unknown = null;
    let timerAktiv = false;
    /** Seit wann der aktuelle Schub wartet — fuer den Hoechstaufschub. */
    let schubSeitMs: number | null = null;
    /** Nach einem Fehlschlag: vorher nicht wieder (ausser beim Anstossen). */
    let wiederholenAbMs = 0;
    let laeuft = false;
    let abgebaut = false;

    const stoppen = () => {
        if (timerAktiv) cancel(timer);
        timer = null;
        timerAktiv = false;
    };

    const planen = () => {
        if (abgebaut || vorgemerkt.size === 0 || opts.istVersteckt()) return;
        const t = jetzt();
        if (schubSeitMs === null) schubSeitMs = t;
        const ziel = Math.max(Math.min(t + SAMMEL_RUHE_MS, schubSeitMs + SAMMEL_HOECHSTENS_MS), wiederholenAbMs);
        stoppen();
        timerAktiv = true;
        timer = schedule(() => {
            timerAktiv = false;
            timer = null;
            void abarbeitenJetzt();
        }, Math.max(0, ziel - t));
    };

    /** Zurueck in die Vormerkung — ausser es kam inzwischen ein neueres Ereignis. */
    const zuruecklegen = (faellig: Map<string, Vormerkung>) => {
        for (const [id, v] of faellig) if (!vorgemerkt.has(id)) vorgemerkt.set(id, v);
    };

    const nachVollLoad = (beginnMs: number) => {
        for (const [id, v] of vorgemerkt) if (v.zuletztMs < beginnMs) vorgemerkt.delete(id);
        if (vorgemerkt.size === 0) { stoppen(); schubSeitMs = null; }
    };

    const abarbeitenJetzt = async (): Promise<void> => {
        if (abgebaut || opts.istVersteckt()) return;
        // Ein Lauf zugleich: zwei parallele Abrufe koennten in falscher
        // Reihenfolge ankommen und einen aelteren Stand zuletzt setzen. Der
        // laufende plant am Ende selbst nach.
        if (laeuft) return;
        const faellig = new Map<string, Vormerkung>();
        for (const [id, v] of vorgemerkt) {
            if (opts.istBekannt(id, v.neu)) { vorgemerkt.delete(id); continue; }
            if (opts.istBeschaeftigt?.(id)) continue;
            faellig.set(id, v);
        }
        schubSeitMs = null;
        if (faellig.size === 0) { planen(); return; }
        for (const id of faellig.keys()) vorgemerkt.delete(id);

        laeuft = true;
        try {
            if (faellig.size > VOLL_LADEN_AB) {
                const voll = opts.vollLaden();
                const beginn = voll ? await voll.catch(() => null) : null;
                if (beginn !== null) {
                    // Was waehrend des Loads gemeldet wurde, ist juenger als er.
                    nachVollLoad(beginn);
                    // Auch aus diesem Schub: teilte sich der Aufruf einen Load,
                    // der schon VOR diesen Meldungen begann, kennt er sie nicht.
                    for (const [id, v] of faellig) if (v.zuletztMs >= beginn && !vorgemerkt.has(id)) vorgemerkt.set(id, v);
                    wiederholenAbMs = 0;
                    return;
                }
                // Takt verbietet ihn oder er ist gescheitert: einzeln weiter.
            }
            const abfrageBeginn = jetzt();
            const eintraege = await opts.laden(faellig);
            // Abgebaut, waehrend der Abruf lief: die Antwort gehoert einer
            // Sitzung, die es nicht mehr gibt (Codex-Review 21.10.2026 —
            // A meldet sich ab, B an, A's Antwort landete in B's Liste).
            if (abgebaut) return;
            opts.uebernehmen(eintraege, abfrageBeginn, faellig);
            wiederholenAbMs = 0;
        } catch (e) {
            if (abgebaut) return;
            console.warn('[realtimeProjectSync] Aktualisierung fehlgeschlagen, wird wiederholt', e);
            zuruecklegen(faellig);
            wiederholenAbMs = jetzt() + SAMMEL_WIEDERHOLUNG_MS;
        } finally {
            laeuft = false;
            planen();
        }
    };

    return {
        melde: (id, neu, fehler) => {
            if (abgebaut) return;
            vorgemerkt.set(id, { neu, fehler, zuletztMs: jetzt() });
            // Verborgen plant `planen` nichts — abgearbeitet wird beim Anstossen.
            planen();
        },
        vergiss: (id) => {
            vorgemerkt.delete(id);
            if (vorgemerkt.size === 0) { stoppen(); schubSeitMs = null; }
        },
        anstossen: () => {
            if (abgebaut || vorgemerkt.size === 0) return;
            stoppen();
            wiederholenAbMs = 0;
            void abarbeitenJetzt();
        },
        nachVollLoad,
        abbauen: () => {
            abgebaut = true;
            stoppen();
            vorgemerkt.clear();
        },
        offen: () => [...vorgemerkt.keys()],
    };
};
